import { motion } from "framer-motion";
import { useState } from "react";

import type { ReportAction } from "@/lib/api";
import { cn } from "@/lib/formatters";

interface ActionsListProps {
  actions: ReportAction[];
}

const TYPE_STYLES: Record<string, { label: string; className: string }> = {
  cannibalization: { label: "cannibalization", className: "text-danger border-danger/40 bg-danger/5" },
  content:         { label: "content",         className: "text-primary border-primary/40 bg-primary/5" },
  new_article:     { label: "new article",     className: "text-success border-success/40 bg-success/5" },
};

const PRIORITY_HUE: Record<number, string> = {
  1: "text-magenta shadow-[0_0_16px_rgba(255,46,136,0.35)] border-magenta/50 bg-magenta/10",
  2: "text-violet shadow-[0_0_12px_rgba(168,85,247,0.25)] border-violet/50 bg-violet/10",
  3: "text-primary border-primary/40 bg-primary/5",
  4: "text-muted border-border bg-bg-elev/40",
};

export function ActionsList({ actions }: ActionsListProps) {
  if (actions.length === 0) {
    return (
      <div className="surface p-6 text-center">
        <p className="font-mono text-sm text-muted">$ no actions this week</p>
      </div>
    );
  }

  const sorted = [...actions].sort((a, b) => a.priority - b.priority);

  return (
    <div className="surface overflow-hidden">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <p className="path-chip">actions</p>
        <span className="font-mono text-xs text-muted">{actions.length} prioritized</span>
      </div>
      <ul className="divide-y divide-border/40">
        {sorted.map((a, i) => (
          <ActionCard key={i} action={a} index={i} />
        ))}
      </ul>
    </div>
  );
}

function ActionCard({ action, index }: { action: ReportAction; index: number }) {
  const [open, setOpen] = useState(action.priority === 1);
  const typeStyle = TYPE_STYLES[action.type] ?? { label: action.type, className: "text-muted border-border bg-bg-elev/40" };
  const priorityStyle = PRIORITY_HUE[action.priority] ?? PRIORITY_HUE[4];

  return (
    <motion.li
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      className="p-4 hover:bg-violet/[0.03] transition-colors"
    >
      <div className="flex items-start gap-3">
        <span className={cn(
          "flex-shrink-0 flex items-center justify-center w-8 h-8 rounded font-mono text-xs font-bold border",
          priorityStyle,
        )}>
          P{action.priority}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cn(
              "inline-block px-1.5 py-0.5 rounded-sm font-mono text-[10px] uppercase border",
              typeStyle.className,
            )}>
              {typeStyle.label}
            </span>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="ml-auto text-muted hover:text-primary font-mono text-xs transition-colors"
            >
              {open ? "collapse ▲" : "expand ▼"}
            </button>
          </div>

          <p className="mt-1.5 text-sm text-text leading-snug">{action.title}</p>

          {action.target && (
            <p className="mt-1 font-mono text-[11px] text-muted/80 truncate" title={action.target}>
              → {action.target}
            </p>
          )}

          {open && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 pt-3 border-t border-border/40"
            >
              <p className="text-sm text-muted/90 leading-relaxed whitespace-pre-line">
                {action.description}
              </p>
            </motion.div>
          )}
        </div>
      </div>
    </motion.li>
  );
}
